import { setData } from 'lib/sequence/model/api/set'
import { MEASUREMENT_KEY } from 'app/xEcoApm/helper'

/**
 * Checks whether `timestamp` is older than `timeToLive` (minutes);
 * if that's not the case, throws an `Error`
 *
 * @param timestamp   the time to check
 * @param timeToLive  the amount of minutes that must elapse before `timestamp` expires
 */
const checkExpiration = (timestamp: string, timeToLive: number) => {
  const now = new Date().valueOf()
  const then = new Date(timestamp).valueOf()
  const diff = Math.abs(now - then)

  const minutesPassed = diff / 1000 / 60

  if (minutesPassed > timeToLive) {
    throw Error(`Measurement cache has expired: ${minutesPassed} > ${timeToLive}`)
  }
}

/**
 * Checks whether the `sensor`'s serial and `serial` are equal;
 * if that's not the case, throws an `Error`.
 *
 * @param sensor  current sensor model
 * @param serial  serial to match
 */
const checkSerial = (sensor: any, serial: number) => {
  const { value: { serial: sensorSerial } } = sensor[G.CACHE]

  if (sensorSerial !== serial) {
    throw Error(`Cached measurement corresponds to another sensor`)
  }
}

/**
 * Loads measurement data into the measurement model, previously cached by
 * {@link cacheMeasurementData}.
 *
 * @param obj current module
 */
const loadCachedMeasurementData = (obj: Gaia.AppModule.Spec) => async (...args: unknown[])=> {
  const actionState = obj[G.STATE][G.ACTION][G.STATE]
  const storage = obj[G.ADAPTER][G.STORAGE]
  const model = obj[G.MODEL]
  const { measurement, sensor } = model[G.CHILDREN]

  const cache = measurement[G.CACHE]?.value || measurement[G.CACHE] || {}
  const measurementData = await storage.get(MEASUREMENT_KEY)

  if (measurementData && !actionState.loadedMeasurementCache) {
    try {
      const { timestamp, serial, data } = JSON.parse(measurementData)

      checkExpiration(timestamp, 1)
      checkSerial(sensor, serial)

      setData(model, { measurement: {
        ...data,
        project: cache.project || data.project,
        customer: cache.customer || data.customer
      } }, { updateCache: true })

      actionState.loadedMeasurementCache = true
    } catch (e) {
      console.warn('Cached measurement data could not be read', e)
      await storage.remove(MEASUREMENT_KEY)
    }
  }

  return args
}

export default loadCachedMeasurementData
